import React, { useEffect, useState } from "react";
import axios from 'axios';
import LessonDetails from "./modalComponents/LessonDetails";
import { Pencil, Trash2, CircleOff, PlusCircle, MinusCircle } from "lucide-react";

export default function CourseManagement() {
    const [showForm, setShowForm] = useState(false);
    const [courseName, setCourseName] = useState('');
    const [totalLessons, setTotalLessons] = useState('');
    const [courseDescription, setCourseDescription] = useState('');
    const [printingMaterials, setPrintingMaterials] =  useState('');
    const [tokenName, setTokenName] = useState('');
    const [tokens, setTokens] = useState('');
    const [courses, setCourses] = useState([]);
    const [showLessonsModal, setShowLessonsModal] = useState(false);
    const [selectedCourseId, setSelectedCourseId] = useState(null);
    const [lessons, setLessons] = useState([]);
    const [selectedLessonId, setSelectedLessonId] = useState(null);
    const [singleLesson, setSingleLesson] = useState([]);
    const [showSingleLessonsModal, setShowSingleLessonsModal] = useState(false);
    const [VideoPackage, setVideoPackage] = useState('');

    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const coursesPerPage = 16;
    const [hideInactive, setHideInactive] = useState(false);

    useEffect(() => {

        fetchCourses();
        fetchToken();
    }, []);

    const fetchToken = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getToken`);
        setTokens(response.data.data);
      } catch (error) {
        console.error('Error fetching token:', error);
        alert('Failed to fetch token.');
      }
    };

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getCourse`);
      setCourses(response.data.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
      alert('Failed to fetch courses.');
    }
  };

  const handleCreateCourse = () => {
      setShowForm(true); // Show the form when the create button is clicked
  };

  const handleCancel = () => {
      setShowForm(false); // Hide the form on cancel
      // Optionally, clear form fields if needed:
      setCourseName('');
      setTokenName('');
      setTotalLessons('');
      setCourseDescription('');
      setPrintingMaterials('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const formData = {
        course_name: courseName,
        total_lessons: totalLessons,
        printing_materials: printingMaterials,
        token_name: tokenName,
        video_package: VideoPackage || '',
        course_description: courseDescription || ''
    };

    try {
        const response = await axios.post(`${process.env.REACT_APP_API_URL}/admin/createCourse`, formData);
        if (response.status === 200) {
            setShowForm(false); // Close form on success
            await fetchCourses(); // Refresh courses table
        }
    } catch (error) {
        console.error('Error creating course:', error);
        alert('Failed to create course.');
    }
    setCourseName('');
    setTokenName('');
    setTotalLessons('');
    setCourseDescription('');
    setPrintingMaterials('');
    setShowForm(false);
  };
  
    const handleManageLessons = async (courseId) => {
        setSelectedCourseId(courseId);
        setShowLessonsModal(true);

        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getLesson/${courseId}`);
            if (response.data.success) {
                setLessons(response.data.data); // Set lessons to an array
            } else {
                setLessons([]); // Set to empty array if no lessons
            }
        } catch (error) {
            console.error('Error fetching lessons:', error);
            setLessons([]); // Ensure lessons is an array even if there’s an error
        }
    };

    const handleManageSingleLesson = async (lessonId) => {
        setSelectedLessonId(lessonId);
        setShowSingleLessonsModal(true);

        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getSingleLesson/${lessonId}`);
            if (response.data.success) {
                setSingleLesson(response.data.data); 
            } else {
                setSingleLesson([]); // Set to empty array if no lessons
            }
        } catch (error) {
            console.error('Error fetching lessons:', error);
        }
    };

    // const handleCloseSingleLessonModal = () => {
    //     setShowSingleLessonsModal(false);
    // };

    const handleSearch = (e) => {
      setSearchQuery(e.target.value.toLowerCase());
      setCurrentPage(1); // reset to page 1 when searching
    };

    const filteredCourses = courses.filter((c) => {
      const matchesSearch = `${c.course_id} ${c.course_name} ${c.token_name}`
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
    
      const isActive = c.status !== 'inactive'; // or whatever your inactive value is
    
      return hideInactive ? matchesSearch && isActive : matchesSearch;
    });  
  
    const indexOfLast = currentPage * coursesPerPage;
    const indexOfFirst = indexOfLast - coursesPerPage;
    const currentCourses = filteredCourses.slice(indexOfFirst, indexOfLast);
    const totalPages = Math.ceil(filteredCourses.length / coursesPerPage);
    
    const handleToggleActive = async (courseId) => {
      await axios.patch(`${process.env.REACT_APP_API_URL}/admin/toggleCourseStatus/${courseId}`);
      fetchCourses();
    }

    const fetchLessons = async (courseId) => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getLessons?course_id=${courseId}`);
        setLessons(response.data.data);
      } catch (error) {
        console.error("Error fetching lessons:", error);
      }
    };

    const handleDeleteCourse = async (courseId) => {
      // Show confirmation prompt
      if (!window.confirm(`Are you sure you want to delete course ${courseId}?`)) {
        return;
      }
  
      try {
        const response = await axios.post(`${process.env.REACT_APP_API_URL}/admin/deleteCourse`, {
          course_id: courseId,
        });
  
        if (response.data.success) {
          alert("Course deleted successfully.");
        } else {
          alert(response.data.message || "Failed to delete course.");
        }
      } catch (error) {
        console.error("Error deleting course:", error);
        alert("An error occurred while deleting the course.");
      }
      fetchCourses();
    };

    const handleAddLesson = async () => {
      if (!window.confirm("Are you sure you want to add a lesson?")) {
        return;
      }
      try {
        const response = await axios.post(`${process.env.REACT_APP_API_URL}/admin/addLesson`, {
          course_id: selectedCourseId,
        });
  
        if (response.data.success) {
          // Refresh lessons list
          await fetchLessons(selectedCourseId);
          alert("Lesson added successfully.");
        } else {
          alert(response.data.message || "Failed to add lesson.");
        }
      } catch (error) {
        console.error("Error adding lesson:", error);
        alert("An error occurred while adding the lesson.");
      }
      fetchLessons(selectedCourseId);
    };
  
    const handleDeleteLesson = async () => {
      if (!window.confirm("Are you sure you want to delete the last lesson?")) {
        return;
      }
  
      try {
        const response = await axios.post(`${process.env.REACT_APP_API_URL}/admin/deleteLesson`, {
          course_id: selectedCourseId,
        });
  
        if (response.data.success) {
          // Refresh lessons list
          await fetchLessons(selectedCourseId);
          alert("Lesson deleted successfully.");
        } else {
          alert(response.data.message || "Failed to delete lesson.");
        }
      } catch (error) {
        console.error("Error deleting lesson:", error);
        alert("An error occurred while deleting the lesson.");
      }
      fetchLessons(selectedCourseId);
    };

  return (
    <div className="p-8 min-h-screen overflow-y-auto">
      <h1 className="text-2xl font-bold">Course Management</h1>
      <div className="mt-2 mb-4 border-t border-gray-400 w-9/12 mx-auto"></div>

       {/* Search Bar */}
       <div className="mb-4 flex flex-col md:flex-row md:items-center md:gap-4">
            <input
              type="text"
              placeholder="Search by name, ID or Token Name"
              value={searchQuery}
              onChange={handleSearch}
              className="w-full md:w-1/3 px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />

            <label className="flex items-center mt-2 md:mt-0">
              <input
                type="checkbox"
                checked={hideInactive}
                onChange={(e) => setHideInactive(e.target.checked)}
                className="mr-2"
              />
              Hide Inactive Courses
            </label>
      </div>
      <div className="flex justify-end gap-2">
        <button 
          onClick={handleCreateCourse}
          className="px-4 py-2 bg-red-400 text-white rounded hover:bg-red-500"
        >
          Create Course
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white w-1/2 max-w-lg rounded-lg shadow-lg p-6 relative">
            <h2 className="text-xl font-semibold mb-4">Create a New Course</h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-gray-700 mb-2" htmlFor="courseName">Course Name</label>
                <input 
                  type="text"
                  id="courseName"
                  value={courseName}
                  onChange={(e) => setCourseName(e.target.value)}
                  className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 mb-2" htmlFor="totalLessons">Total Lessons</label>
                <input 
                  type="number"
                  id="totalLessons"
                  value={totalLessons}
                  onChange={(e) => setTotalLessons(e.target.value)}
                  className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 mb-2" htmlFor="tokenName">Token</label>
                <select
                  id="tokenName"
                  value={tokenName}
                  onChange={(e) => setTokenName(e.target.value)}
                  className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="" disabled>Select a token</option>
                  {tokens.map((token) => (
                    <option key={token.token_id} value={token.token_name}>
                      {token.token_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 mb-2" htmlFor="printingMaterials">Printing Materials</label>
                <input
                  id="printingMaterials"
                  value={printingMaterials}
                  onChange={(e) => setPrintingMaterials(e.target.value)}
                  className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 mb-2" htmlFor="VideoPackage">
                  VideoPackage
                </label>
                <select
                  id="VideoPackage"
                  value={VideoPackage}
                  onChange={(e) => setVideoPackage(e.target.value)}
                  className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">{null}</option> {/* Default "None" option */}
                  {courses
                    .filter((course) =>
                      course.course_name.toLowerCase().startsWith('vc') // Case-insensitive filtering
                    )
                    .map((course) => (
                      <option key={course.course_id} value={course.course_name}>
                        {course.course_name}
                      </option>
                    ))}
                </select>
              </div>
              <div className="mb-6">
                <label className="block text-gray-700 mb-2" htmlFor="courseDescription">Course Description (Optional)</label>
                <textarea
                  id="courseDescription"
                  value={courseDescription}
                  onChange={(e) => setCourseDescription(e.target.value)}
                  className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="4"
                />
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="bg-gray-500 text-white px-4 py-2 mr-2 rounded hover:bg-gray-600">
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600">
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <table className="w-full border-collapse bg-white">
        <thead>
          <tr className="bg-gray-200">
            {/* <th className="border px-4 py-2">Course ID</th> */}
            <th className="border px-4 py-2">Course Name</th>
            <th className="border px-4 py-2">Total Lessons</th>
            <th className="border px-4 py-2">Printing Materials</th>
            <th className="border px-4 py-2">Token</th>
            <th className="border px-4 py-2">Description</th>
            <th className="border px-4 py-2">Video Package</th>
            <th className="border px-4 py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {currentCourses.map((course) => (
            <tr key={course.course_id} className="text-center border-t">
              {/* <td className="px-4 py-2 border">{course.course_id}</td> */}
              <td className="px-4 py-2 border">{course.course_name}</td>
              <td className="px-4 py-2 border">{course.total_lessons}</td>
              <td className="px-4 py-2 border">{course.printing_materials}</td>
              <td className="px-4 py-2 border">{course.token_name}</td>
              <td className="px-4 py-2 border">{course.course_description}</td>
              <td className="px-4 py-2 border">{course.video_package}</td>
              <td className="px-4 py-2 border">
                <div className="flex justify-center gap-2">
                  <button 
                    onClick={() => handleManageLessons(course.course_id)}>
                     <Pencil className="w-5 h-5 text-blue-600 hover:text-blue-800" />
                  </button> 
                  <button
                    onClick={() => handleDeleteCourse(course.course_id)}>
                    <Trash2 className="w-5 h-5 text-red-600 hover:text-red-800" />
                  </button>
                  <button onClick={() => handleToggleActive(course.course_id)}>
                    <CircleOff
                      className={`w-5 h-5 ${
                        course.status === 'active' ? "text-green-600" : "text-gray-400"
                      } hover:text-green-800`}
                    />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {/* Modal for Managing Lessons */}
      {showLessonsModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg w-3/4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Manage Lessons for Course ID: {selectedCourseId}</h3>
              <div className="flex gap-2">
                <button
                  onClick={handleAddLesson}
                  className="bg-green-500 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-green-600"
                >
                  <PlusCircle className="w-5 h-5" />
                  Add a Lesson
                </button>
                <button
                  onClick={handleDeleteLesson}
                  className="bg-red-500 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-red-600"
                >
                  <MinusCircle className="w-5 h-5" />
                  Delete Last Lesson
                </button>
              </div>
            </div>
            <table className="min-w-full bg-white border border-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-2 border">Lesson ID</th>
                  <th className="px-4 py-2 border">Course ID</th>
                  <th className="px-4 py-2 border">Actions</th>
                </tr>
              </thead>
              <tbody>
                {lessons.map((lesson) => (
                  <tr key={lesson.lesson_id}>
                    <td className="px-4 py-2 border">{lesson.lesson_id}</td>
                    <td className="px-4 py-2 border">{lesson.course_id}</td>
                    <td className="px-4 py-2 border space-x-2">
                      <button onClick={() => handleManageSingleLesson(lesson.lesson_id)}>
                        <Pencil className="w-5 h-5 text-blue-600 hover:text-blue-800" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button
              onClick={() => setShowLessonsModal(false)}
              className="mt-4 bg-gray-500 text-white px-4 py-2 rounded"
            >
              Close
            </button>
          </div>
        </div>
      )}
        {showSingleLessonsModal && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
                <div className="bg-white p-6 rounded-lg w-1/2">
                    <h3 className="text-xl font-semibold mb-4">Manage Lesson for Lesson ID : {selectedLessonId} </h3>
                    {singleLesson ? ( // Check if singleLesson has data
                        <LessonDetails lesson={singleLesson} onClose={() => setShowSingleLessonsModal(false)} />
                    ) : (
                        <p>Loading lesson data...</p> // Optional: Add a fallback for clarity
                    )}
                </div>
            </div>
        )}
        {/* Pagination Controls */}
        <div className="flex justify-between items-center mt-6">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            className="px-3 py-1 bg-gray-300 text-gray-800 rounded disabled:opacity-50"
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <select
            value={currentPage}
            onChange={(e) => setCurrentPage(Number(e.target.value))}
            className="px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
              <option key={page} value={page}>
                Page {page} of {totalPages}
              </option>
            ))}
          </select>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            className="px-3 py-1 bg-gray-300 text-gray-800 rounded disabled:opacity-50"
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
    </div>
  );
}

